/**
 * Which edge banding goes with a board — the rule every item dialog applies the
 * moment a material is picked, shared here so the server can apply the very
 * same rule when it swaps materials in bulk (the BOM's "edit material").
 *
 *   - Painted MDF (`MDF_VOPSIT`) takes no banding at all.
 *   - Otherwise the candidates are the edges whose name or manufacturer names
 *     the board's manufacturer; a board with no manufacturer has none.
 *   - Candidates rank by the product of the numbers in their name ("22x0.8"
 *     → 17.6) — smallest first, or largest first for a thicker (doubled)
 *     board — then by price, then by name.
 *
 * Structurally typed so the web passes its list items and the API its Prisma
 * rows unchanged.
 */

export interface EdgeAutoSelectMaterial {
  category: string;
  manufacturer: string | null;
}

export interface EdgeAutoSelectCandidate {
  id: string;
  name: string;
  manufacturer: string | null;
  pricePerLm?: string | null;
}

function normalize(value: string | null | undefined): string {
  return (value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function edgeMatchesManufacturer(edge: EdgeAutoSelectCandidate, manufacturer: string): boolean {
  const normalizedManufacturer = normalize(manufacturer);
  if (!normalizedManufacturer) return false;

  const edgeManufacturer = normalize(edge.manufacturer);
  const edgeName = normalize(edge.name);
  return (
    edgeName.includes(normalizedManufacturer) ||
    edgeManufacturer.includes(normalizedManufacturer) ||
    (edgeManufacturer.length > 0 && normalizedManufacturer.includes(edgeManufacturer))
  );
}

function edgeProductValue(edge: EdgeAutoSelectCandidate): number | null {
  const numbers = edge.name.match(/\d+(?:[.,]\d+)?/g);
  if (!numbers?.length) return null;
  return numbers
    .map((value) => Number(value.replace(',', '.')))
    .filter(Number.isFinite)
    .reduce((product, value) => product * value, 1);
}

function edgePrice(edge: EdgeAutoSelectCandidate): number {
  const parsed = Number(edge.pricePerLm);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** True when the board takes no banding by rule (painted MDF). */
export function materialTakesNoEdge(material: EdgeAutoSelectMaterial): boolean {
  return material.category === 'MDF_VOPSIT';
}

export function chooseEdgeForMaterial<TEdge extends EdgeAutoSelectCandidate>(
  material: EdgeAutoSelectMaterial,
  edges: TEdge[],
  options: { preferLargestProduct?: boolean } = {},
): TEdge | null {
  if (materialTakesNoEdge(material)) return null;
  const manufacturer = material.manufacturer?.trim();
  if (!manufacturer) return null;

  const candidates = edges
    .filter((edge) => edgeMatchesManufacturer(edge, manufacturer))
    .map((edge) => ({ edge, productValue: edgeProductValue(edge) }));
  if (candidates.length === 0) return null;

  const direction = options.preferLargestProduct ? -1 : 1;
  candidates.sort((a, b) => {
    const aProduct = a.productValue;
    const bProduct = b.productValue;
    if (aProduct !== null && bProduct !== null && aProduct !== bProduct) {
      return (aProduct - bProduct) * direction;
    }
    if (aProduct !== null && bProduct === null) return -1;
    if (aProduct === null && bProduct !== null) return 1;

    const priceDiff = edgePrice(a.edge) - edgePrice(b.edge);
    if (priceDiff !== 0) return priceDiff;
    return a.edge.name.localeCompare(b.edge.name);
  });
  return candidates[0]?.edge ?? null;
}
